import { useState } from "react"
import { Box, Checkbox, Chip, IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Classification } from "routes/schedule/tasks/classification"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { SkillSetLabel } from "authoring/routes/macro/plugins/component-ref-skillset-label"
import { MoveTaskDialog } from "./move-task-dialog"
import { DraftChip } from "./draft-chip"

const TASK_TYPE_COLORS = {
    operational: "info",
    maintenance: "primary",
    specialist: "warning",
}

export function TaskRow({ item }) {
    const { readOnly, setSelectedTaskId, refresh, category, embedded } = useBoundContext()
    const [moveOpen, setMoveOpen] = useState(false)
    const timingDisplay = item.timing?.minutes ? `${item.timing.minutes} min` : null
    const rowSelector = embedded ? "component-task-row" : `task-row: ${item.heading}`

    return (
        <>
            <TableRow
                hover
                sx={{ cursor: "pointer", ...(embedded && { "& > td": { py: 1.5 } }) }}
                onClick={handleClick}
                data-cy={rowSelector}
            >
                <If value={embedded}>
                    <TableCell>
                        <Typography variant="subtitle2">{item.heading}</Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Classification classification={item.classification} />
                        </Box>
                    </TableCell>
                    <TableCell align="center">
                        <Chip
                            label={item.frequencyCode || item.frequency?.code || "—"}
                            size="small"
                            variant="outlined"
                        />
                    </TableCell>
                    <TableCell align="center">
                        <If value={item.skillSet}>
                            <Box sx={{ display: "flex", justifyContent: "center", "& > div": { py: 0.25 } }}>
                                <SkillSetLabel skillSetCode={item.skillSet} />
                            </Box>
                            <Else>—</Else>
                        </If>
                    </TableCell>
                    <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                            {timingDisplay || "—"}
                        </Typography>
                    </TableCell>

                    <Else>
                        <TableCell>
                            <Stack direction="row" alignItems="center">
                                <Typography variant="subtitle2">{item.heading}</Typography>
                                <DraftChip item={item} />
                            </Stack>
                        </TableCell>
                        <TableCell>
                            <Classification classification={item.classification} />
                        </TableCell>
                        <TableCell>
                            <Chip
                                label={item.frequencyCode || item.frequency?.code || "—"}
                                size="small"
                                variant="outlined"
                            />
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2">{item.skillSet || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                            <If value={item.taskType}>
                                <Chip
                                    label={item.taskType}
                                    size="small"
                                    color={TASK_TYPE_COLORS[item.taskType] || "default"}
                                    variant="soft"
                                />
                            </If>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="body2" color="text.secondary">
                                {timingDisplay || "—"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Checkbox
                                checked={!!item.actionsHtml}
                                size="small"
                                disableRipple
                                sx={{ pointerEvents: "none" }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Checkbox
                                checked={!!item.notesHtml}
                                size="small"
                                disableRipple
                                sx={{ pointerEvents: "none" }}
                            />
                        </TableCell>
                        <If truthy={!readOnly}>
                            <TableCell align="center" sx={{ width: 48 }}>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={handleMoveClick}
                                    aria-label="Move component"
                                >
                                    <Iconify icon="eva:move-outline" width={16} height={16} />
                                </IconButton>
                            </TableCell>
                        </If>
                    </Else>
                </If>
            </TableRow>

            <If truthy={!readOnly}>
                <MoveTaskDialog
                    open={moveOpen}
                    onClose={handleCloseMove}
                    task={item}
                    categoryId={category?.id}
                    onMoved={handleMoved}
                />
            </If>
        </>
    )

    function handleCloseMove() {
        setMoveOpen(false)
    }

    function handleClick() {
        setSelectedTaskId(item.id)
    }

    function handleMoveClick(e) {
        e.stopPropagation()
        setMoveOpen(true)
    }

    function handleMoved() {
        setMoveOpen(false)
        refresh()
    }
}
